import { useState, useCallback, useEffect, useRef } from 'react';
import api from '../services/api';
import { POLL_INTERVALS } from '../config/appPolling';

const EMPTY_BADGE = { approve: 0, cc: 0, execute: 0, initiated: 0, drafts: 0, total: 0 };

export function useApprovalNotify(enabled) {
  const [badge, setBadge] = useState(EMPTY_BADGE);
  const [popups, setPopups] = useState([]);
  const [visiblePopups, setVisiblePopups] = useState([]);
  const shownIdsRef = useRef(new Set());
  const initialFetchedRef = useRef(false);

  const fetchSummary = useCallback(async (isInitial = false) => {
    if (!enabled) return;
    try {
      const res = await api.get('/approval-center/summary');
      if (!res.data.success) return;
      const { badge: b, popups: list } = res.data.data;
      setBadge(b || EMPTY_BADGE);

      const pending = list || [];
      setPopups(pending);

      const fresh = pending.filter((n) => !shownIdsRef.current.has(n.id));
      if (fresh.length) {
        fresh.forEach((n) => shownIdsRef.current.add(n.id));
        setVisiblePopups((prev) => {
          const merged = [...prev];
          fresh.forEach((item) => {
            if (!merged.some((m) => m.id === item.id)) merged.push(item);
          });
          return merged;
        });
      }

      if (isInitial && !initialFetchedRef.current) {
        initialFetchedRef.current = true;
      }
    } catch {
      /* 静默失败，避免打断用户操作 */
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setBadge(EMPTY_BADGE);
      setPopups([]);
      setVisiblePopups([]);
      shownIdsRef.current = new Set();
      initialFetchedRef.current = false;
      return undefined;
    }

    fetchSummary(true);
    const timer = setInterval(() => fetchSummary(false), POLL_INTERVALS.approvalNotify);
    return () => clearInterval(timer);
  }, [enabled, fetchSummary]);

  const dismissPopup = useCallback(async (notifyId) => {
    setVisiblePopups((prev) => prev.filter((p) => p.id !== notifyId));
    try {
      await api.post('/approval-center/popups/ack', { notifyIds: [notifyId] });
    } catch {
      /* ignore */
    }
  }, []);

  const dismissAllPopups = useCallback(async () => {
    const ids = visiblePopups.map((p) => p.id);
    setVisiblePopups([]);
    if (ids.length) {
      try {
        await api.post('/approval-center/popups/ack', { notifyIds: ids });
      } catch {
        /* ignore */
      }
    }
  }, [visiblePopups]);

  const refresh = useCallback(() => fetchSummary(false), [fetchSummary]);

  return {
    badge,
    popups,
    visiblePopups,
    dismissPopup,
    dismissAllPopups,
    refresh
  };
}
